import type { MenuProps } from 'antd'
import { Dropdown } from 'antd'
import { ExternalLink, FolderOpen } from 'lucide-react'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

const TriggerAnchor = styled.div<{ $x: number; $y: number }>`
  position: fixed;
  left: ${(props) => props.$x}px;
  top: ${(props) => props.$y}px;
  width: 1px;
  height: 1px;
  pointer-events: none;
`

const MenuLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const MenuShortcut = styled.span`
  opacity: 0.6;
  font-size: 0.75rem;
`

export type ContextMenuProps = {
  position: { x: number; y: number } | null
  onClose: () => void
  onOpen: () => void
  onOpenInNewTab: () => void
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  position,
  onClose,
  onOpen,
  onOpenInNewTab,
}) => {
  const handleOpen = useCallback(() => {
    onOpen()
    onClose()
  }, [onOpen, onClose])

  const handleOpenInNewTab = useCallback(() => {
    onOpenInNewTab()
    onClose()
  }, [onOpenInNewTab, onClose])

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'open',
        icon: <FolderOpen size={16} />,
        label: 'Open',
        onClick: handleOpen,
      },
      {
        key: 'open-new-tab',
        icon: <ExternalLink size={16} />,
        label: (
          <MenuLabel>
            Open in New Tab
            <MenuShortcut>⌘+Click</MenuShortcut>
          </MenuLabel>
        ),
        onClick: handleOpenInNewTab,
      },
    ],
    [handleOpen, handleOpenInNewTab]
  )

  if (!position) {
    return null
  }

  return (
    <Dropdown
      menu={{ items: menuItems }}
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      trigger={['click']}>
      <TriggerAnchor $x={position.x} $y={position.y} />
    </Dropdown>
  )
}
